import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, CircleIcon, LockIcon, PlayCircleIcon, RefreshCwIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Seo } from '../../components/Seo';
import { JoinDialog } from '../../components/academy/JoinDialog';
import { ProgressBar } from '../../components/academy/ProgressBar';
import { CourseCard } from '../../components/academy/CourseCard';
import { ACADEMY_LINES } from '../../data/academyCourses';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { courseProgress, lessonKey, lessonPath, moduleProgress, nextLesson, totalLessons } from '../../utils/academyProgress';
import type { Learner } from '../../types/academy';
import { NotFound } from '../NotFound';
export function AcademyCourse() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    findCourse,
    publishedCourses,
    hub,
    isPublished
  } = useAcademyHub();
  const course = findCourse(slug ?? '');
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const {
    state,
    isEnrolled,
    enrol
  } = useAcademy();
  const [open, setOpen] = React.useState<number[]>([0]);
  const [joinOpen, setJoinOpen] = React.useState(false);
  const [justEnrolled, setJustEnrolled] = React.useState(false);
  if (!course) return <NotFound data-id="element-1635" data-mp="29s10b72xq0gb" />;
  const enrolled = isEnrolled(course.slug);
  const progress = courseProgress(course, state);
  const next = nextLesson(course, state);
  const line = ACADEMY_LINES.find(l => l.id === course.serviceLine)?.name;
  const related = publishedCourses.filter(c => c.slug !== course.slug && c.serviceLine === course.serviceLine).slice(0, 3);
  const update = hub.courseUpdates[course.slug];
  // Learners who enrolled before the latest update see what changed.
  const showUpdate = enrolled && update && update.at > (state.enrolments[course.slug] ?? '');
  const doEnrol = async (learner?: Learner) => {
    await enrol(course, learner);
    setJoinOpen(false);
    setJustEnrolled(true);
  };
  const onEnrolClick = () => {
    if (state.learner) void doEnrol();else setJoinOpen(true);
  };
  const toggle = (index: number) => setOpen(current => current.includes(index) ? current.filter(i => i !== index) : [...current, index]);
  return <>
      <Seo title={`${course.title}, free course`} description={course.description} path={`/academy/courses/${course.slug}`} data-id="element-1636" data-mp="bhacyacsy6c4" />

      <section className="border-b border-line" data-id="element-1637" data-mp="6hfjq05k14nw">
        <Container className="py-12 sm:py-16" data-id="element-1638" data-mp="1jmh1h3gmssd">
          {!isPublished(course.slug) && <p className="mb-6 rounded-sm border border-dashed border-gold/60 px-4 py-3 text-[14px] text-body" data-id="element-1639" data-mp="1m06nafdfcsj">
              Draft. Learners cannot see this course until a platform admin publishes it.
            </p>}
          {showUpdate && <div role="status" className="mb-6 flex items-start gap-3 rounded-sm border border-line bg-panel px-4 py-3 text-[14px] text-body" data-id="element-1640" data-mp="2c7960v6ibjhy">
              <RefreshCwIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1641" data-mp="2m445bgaltnxa" />
              <p data-id="element-1642" data-mp="2m29hnu7y7hpi">
                <span className="text-ink" data-id="element-1643" data-mp="2qukl371wdlmt">This course has been updated. Your progress is preserved.</span> {update.note}
              </p>
            </div>}
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1644" data-mp="2uyigp7s0keb4">
            <ol className="flex flex-wrap items-center gap-2 text-small text-muted" data-id="element-1645" data-mp="2zuhd9inhx2mm">
              <li data-id="element-1646" data-mp="34uc3rl3czq75">
                <Link to="/academy" className="hover:text-ink" data-id="element-1647" data-mp="34vwybapob950">
                  Courses
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1648" data-mp="39rm0llt2v450">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1649" data-mp="1ah43vf0j0oen" />
              </li>
              <li aria-current="page" className="truncate text-body" data-id="element-1650" data-mp="1ffnsjn6i8eap">
                {course.title}
              </li>
            </ol>
          </nav>
          <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:items-start" data-id="element-1651" data-mp="35vg7retwqcx2">
            <div data-id="element-1652" data-mp="30yhfz2xpui21">
              <p className="text-small font-medium text-gold" data-id="element-1653" data-mp="30wwmlgn5155r">
                Cavalla {line} · Course {course.number}
              </p>
              <h1 className="mt-3 max-w-3xl font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-1654" data-mp="3ev3aq52abqs8">{course.title}</h1>
              <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-body" data-id="element-1655" data-mp="2m8a79exigdej">{course.description}</p>
              <dl className="mt-8 flex flex-wrap gap-x-10 gap-y-4 text-[15px]" data-id="element-1656" data-mp="2i7q8y817b3qt">
                <Meta label="Level" value={course.difficulty} data-id="element-1657" data-mp="2vybktcs8mdf7" />
                <Meta label="Time" value={`${course.hours} hours`} data-id="element-1658" data-mp="2vzwg54mdm0e4" />
                <Meta label="Modules" value={`${course.modules.length} modules, ${totalLessons(course)} lessons`} data-id="element-1659" data-mp="2r6tt7fc3tp6w" />
                <Meta label="Sectors" value={course.allSectors ? 'All sectors' : course.sectors.join(', ')} data-id="element-1660" data-mp="23gpr5cekdxi0" />
              </dl>
            </div>

            <aside className="rounded-md border border-line bg-panel p-6 lg:sticky lg:top-28" aria-label="Enrolment" data-id="element-1661" data-mp="e1f3hdbvbcrb">
              {enrolled ? <div data-id="element-1662" data-mp="itgbgn206hbj">
                  <p className="text-small text-body" aria-live="polite" data-id="element-1663" data-mp="itzw4bj1fjt9">
                    {justEnrolled ? 'You are enrolled. Every module is now unlocked.' : progress.complete ? 'Course complete' : 'You are enrolled'}
                  </p>
                  <p className="mt-3 font-display text-[36px] leading-none text-ink" data-id="element-1664" data-mp="xomfx5lga82r">{progress.percent}%</p>
                  <ProgressBar className="mt-4" percent={progress.percent} complete={progress.complete} label="Course progress" data-id="element-1665" data-mp="11r0ydol7j5ti" />
                  <p className="mt-3 text-small text-muted" data-id="element-1666" data-mp="nqglf91z447x">
                    {progress.completedModules} of {progress.totalModules} modules complete
                  </p>
                  {progress.complete && state.certificates[course.slug] ? <Button to={`/academy/certificates/${state.certificates[course.slug].id}`} size="lg" className="mt-6 w-full" data-id="element-1667" data-mp="3dgtl1q0bbame">
                      View your certificate
                    </Button> : <Button to={lessonPath(course.slug, next.moduleIndex, next.lessonIndex)} size="lg" className="mt-6 w-full" data-id="element-1668" data-mp="2pdvm5c7vh570">
                      {progress.started ? 'Continue learning' : 'Start the first lesson'}
                    </Button>}
                </div> : <div data-id="element-1669" data-mp="2aohfibvol1vb">
                  <p className="font-display text-[22px] text-ink" data-id="element-1670" data-mp="2allci94z7n00">Free to enrol</p>
                  <p className="mt-2 text-[14px] text-body" data-id="element-1671" data-mp="25n1taghropbc">
                    Module 1 is open to everyone. Enrol to unlock the remaining {course.modules.length - 1} modules, the quizzes and
                    your certificate.
                  </p>
                  <Button type="button" size="lg" className="mt-6 w-full" onClick={onEnrolClick} data-id="element-1672" data-mp="3w1kg6v3h4bkq">
                    Enrol for free
                  </Button>
                  <Link to={lessonPath(course.slug, 0, 0)} className="mt-3 flex items-center justify-center gap-2 py-2 text-[14px] text-royal underline underline-offset-4 hover:text-ink" data-id="element-1673" data-mp="3qzv0v397xbqu">
                    <PlayCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-1674" data-mp="c5m8v9tbsrts" />
                    Preview Module 1
                  </Link>
                </div>}
            </aside>
          </div>
        </Container>
      </section>

      <section aria-labelledby="curriculum" data-id="element-1675" data-mp="3fofjbdqt50jq">
        <Container className="py-14 sm:py-16" data-id="element-1676" data-mp="3kl60y5i9g479">
          <div className="max-w-3xl" data-id="element-1677" data-mp="3klxxbijom5yl">
            <h2 id="curriculum" className="font-display text-[26px] text-ink" data-id="element-1678" data-mp="3pl0qo5qaunqq">
              Curriculum
            </h2>
            <p className="mt-2 text-[15px] text-body" data-id="element-1679" data-mp="3tmm9s8tffy9d">
              Each module ends with a short quiz. A module is complete once every lesson is done and the quiz is passed.
            </p>

            <ol className="mt-8 divide-y divide-line border-y border-line" data-id="element-1680" data-mp="18vlqdtrgf0oi">
              {course.modules.map((mod, mi) => {
              const mp = moduleProgress(course, mi, state);
              const unlocked = mi === 0 || enrolled;
              const isOpen = open.includes(mi);
              return <li key={mod.title} data-id="element-1681" data-mp="24ojngg2cezpe" data-mpi={mi}>
                    <h3 data-id="element-1682" data-mp="24pbb94pp7t54">
                      <button type="button" aria-expanded={isOpen} aria-controls={`module-${mi}`} onClick={() => toggle(mi)} className="flex w-full items-center gap-4 py-5 text-left" data-id="element-1683" data-mp="1zx1ofxjvr972">
                        <span className="w-6 shrink-0 font-display text-[15px] text-muted" data-id="element-1684" data-mp="2nlb74qlsrgtz">{mi + 1}</span>
                        <span className="min-w-0 flex-1" data-id="element-1685" data-mp="2iokru5htgp9m">
                          <span className="block text-[17px] font-medium text-ink" data-id="element-1686" data-mp="t81skkee954v">{mod.title}</span>
                          <span className="mt-1 block text-small text-muted" data-id="element-1687" data-mp="o9i6mjzsvtci">
                            {mod.lessons.length} lessons
                            {mi === 0 && !enrolled && <span className="ml-2 text-gold" data-id="element-1688" data-mp="2zxdhqxqkc5qd">Free preview</span>}
                            {!unlocked && <span className="ml-2" data-id="element-1689" data-mp="34uvii0uffena">Enrol to unlock (free)</span>}
                            {enrolled && mp.done > 0 && !mp.complete && <span className="ml-2" data-id="element-1690" data-mp="2v53nj5if0jia">
                                {mp.done} of {mp.total} done
                              </span>}
                          </span>
                        </span>
                        {mp.complete ? <CheckCircle2Icon className="h-5 w-5 shrink-0 text-[#4ADE80]" aria-label="Complete" data-id="element-1691" data-mp="2786drj8canxq" /> : !unlocked ? <LockIcon className="h-4 w-4 shrink-0 text-muted" aria-label="Locked" data-id="element-1692" data-mp="2c79g2vwxqg20" /> : null}
                        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1693" data-mp="afmksi60fyzq" />
                      </button>
                    </h3>
                    <AnimatePresence initial={false} data-id="element-1694" data-mp="3pcp7aka9pmn8">
                      {isOpen && <motion.div id={`module-${mi}`} initial={reduced ? false : {
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={reduced ? undefined : {
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="overflow-hidden" data-id="element-1695" data-mp="3pavxiw4cymb0">
                          <ul className="pb-5 pl-10" data-id="element-1696" data-mp="gv2h0ha11aty">
                            {mod.lessons.map((lesson, li) => {
                        const done = Boolean(state.completions[lessonKey(course.slug, mi, li)]);
                        return <li key={lesson} data-id="element-1697" data-mp="2u2tz7qdndg0u" data-mpi={li}>
                                  {unlocked ? <Link to={lessonPath(course.slug, mi, li)} className="flex items-start gap-3 rounded-sm px-3 py-2.5 text-[15px] text-body transition-colors duration-150 hover:bg-panel hover:text-ink" data-id="element-1698" data-mp="2p5brg3bd96uf">
                                      {done ? <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#4ADE80]" aria-label="Completed" data-id="element-1699" data-mp="26ekhp0ngvefi" /> : <CircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden="true" data-id="element-1700" data-mp="2fepekgv1auff" />}
                                      {lesson}
                                    </Link> : <button type="button" onClick={onEnrolClick} className="flex w-full items-start gap-3 rounded-sm px-3 py-2.5 text-left text-[15px] text-muted transition-colors duration-150 hover:bg-panel" data-id="element-1701" data-mp="1mo7dfldvf34n">
                                      <LockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1702" data-mp="5b05i8dqwodh" />
                                      {lesson}
                                    </button>}
                                </li>;
                      })}
                            <li className="px-3 pt-2 text-small text-muted" data-id="element-1703" data-mp="3dtz8dupbmnmk">Quiz: {mod.quizFocus}</li>
                          </ul>
                        </motion.div>}
                    </AnimatePresence>
                  </li>;
            })}
            </ol>
          </div>
        </Container>
      </section>

      {related.length > 0 && <section aria-labelledby="related" className="border-t border-line bg-mist" data-id="element-1704" data-mp="2hn4cklm2m9le">
          <Container className="py-14" data-id="element-1705" data-mp="2djy6pf2vekfg">
            <h2 id="related" className="font-display text-[22px] text-ink" data-id="element-1706" data-mp="28sg8gys8exy6">
              More from Cavalla {line}
            </h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1707" data-mp="23sk4v0ng58j5">
              {related.map(c => <CourseCard key={c.slug} course={c} data-id="element-1708" data-mp="1yvbn3167n6if" />)}
            </div>
          </Container>
        </section>}

      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} courseTitle={course.title} onJoined={learner => {
      void doEnrol(learner);
      navigate(`/academy/courses/${course.slug}`, {
        replace: true
      });
    }} data-id="element-1709" data-mp="1txs6vvpycjnu" />
    </>;
}
function Meta({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-1710" data-mp="3f9bhxxq785yt">
      <dt className="text-small text-muted" data-id="element-1711" data-mp="3f9jshiyumz6u">{label}</dt>
      <dd className="mt-1 text-ink" data-id="element-1712" data-mp="2bxmj9b0xzcnv">{value}</dd>
    </div>;
}